import React from 'react';
import V2Card from './V2Card';
import V2Badge from '../ui/V2Badge';
import V2Button from '../forms/V2Button';
import '../../../v2-theme.css';

/**
 * ShiftCard Component
 *
 * Specialized card for displaying shift information
 * Matches shift card design from HTML files
 *
 * @param {Object} shift - Shift data object
 * @param {string} userType - 'worker' | 'restaurant'
 * @param {function} onClaim - Claim shift handler (for workers)
 * @param {function} onCancel - Cancel shift handler
 * @param {function} onDelete - Delete shift handler (for restaurants)
 * @param {function} onViewProfile - View worker profile handler
 * @param {boolean} isClaimed - Whether shift is already claimed
 */
const ShiftCard = ({
  shift,
  userType = 'worker',
  onClaim,
  onCancel,
  onDelete,
  onViewProfile,
  isClaimed = false,
  className = '',
  ...props
}) => {
  const {
    position,
    hourly_rate,
    shift_date,
    start_time,
    end_time,
    location,
    notes,
    status,
    restaurant_profiles,
    applications
  } = shift;

  // Get restaurant name from nested object or fallback
  const restaurantName = restaurant_profiles?.business_name || shift.restaurant_name || 'Unknown Restaurant';

  // Get worker name from applications if present
  const workerName = applications?.[0]?.worker_profiles ?
    `${applications[0].worker_profiles.first_name} ${applications[0].worker_profiles.last_name}` :
    'No worker assigned';

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatTime = (timeStart, timeEnd) => {
    if (!timeStart || !timeEnd) return 'No time specified';

    // Time comes as "HH:MM:SS" format
    const parseTime = (timeStr) => {
      const [hours, minutes] = timeStr.split(':');
      return { hours: parseInt(hours, 10), minutes: parseInt(minutes, 10) };
    };

    const start = parseTime(timeStart);
    const end = parseTime(timeEnd);

    // Calculate hours difference
    const startMinutes = start.hours * 60 + start.minutes;
    const endMinutes = end.hours * 60 + end.minutes;
    const diffMinutes = endMinutes - startMinutes;
    const hours = (diffMinutes / 60).toFixed(1);

    // Format as 12-hour time
    const format12Hour = (time) => {
      const ampm = time.hours >= 12 ? 'PM' : 'AM';
      const displayHour = time.hours % 12 || 12;
      const displayMinutes = time.minutes.toString().padStart(2, '0');
      return `${displayHour}:${displayMinutes} ${ampm}`;
    };

    return `${format12Hour(start)} - ${format12Hour(end)} (${hours} hrs)`;
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'open':
        return <V2Badge variant="secondary">Open</V2Badge>;
      case 'filled':
        return <V2Badge variant="success">Claimed</V2Badge>;
      case 'completed':
        return <V2Badge variant="success">Completed</V2Badge>;
      case 'cancelled':
        return <V2Badge variant="danger">Cancelled</V2Badge>;
      default:
        return null;
    }
  };

  return (
    <V2Card className={`v2-shift-card ${className}`} {...props}>
      <div className="v2-shift-header">
        <div className="v2-shift-position">{position}</div>
        <div className="v2-shift-pay">${hourly_rate ? parseFloat(hourly_rate).toFixed(2) : '0.00'}/hr</div>
      </div>

      <div className="v2-restaurant-name">
        {userType === 'worker' ? restaurantName : workerName}
      </div>

      <div className="v2-shift-details">
        <div><strong>Date:</strong> {formatDate(shift_date)}</div>
        <div><strong>Time:</strong> {formatTime(start_time, end_time)}</div>
        {location && <div><strong>Location:</strong> {location}</div>}
        {status && <div>{getStatusBadge()}</div>}
      </div>

      {notes && (
        <div className="v2-shift-notes">
          {notes}
        </div>
      )}

      <div className="v2-shift-actions">
        {userType === 'worker' && status === 'open' && !isClaimed && (
          <V2Button
            variant="secondary"
            fullWidth
            onClick={() => onClaim && onClaim(shift)}
          >
            Claim Shift
          </V2Button>
        )}

        {userType === 'worker' && isClaimed && (
          <V2Button
            variant="outline"
            fullWidth
            onClick={() => onCancel && onCancel(shift)}
          >
            Cancel
          </V2Button>
        )}

        {userType === 'restaurant' && status === 'claimed' && (
          <V2Button
            variant="secondary"
            fullWidth
            onClick={() => onViewProfile && onViewProfile(shift)}
          >
            View Worker Profile
          </V2Button>
        )}

        {userType === 'restaurant' && status === 'open' && (
          <V2Button
            variant="danger"
            fullWidth
            onClick={() => onDelete && onDelete(shift)}
          >
            Delete Shift
          </V2Button>
        )}
      </div>
    </V2Card>
  );
};

export default ShiftCard;
